import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { waLink } from "../lib/site";
import { useChatStream } from "../hooks/useChatStream";
import { EASE } from "./Motion";

const SUGGESTIONS = [
  "What is your MOQ?",
  "Do you offer private label?",
  "What are your payment terms?",
  "Can I get a COA?",
];

function MessageBubble({ msg, showCursor }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser ? "bg-forest text-offwhite" : "bg-white/[0.06] text-offwhite/85 border border-white/10"
        }`}
      >
        {msg.content}
        {showCursor && <span className="inline-block w-1.5 h-3.5 bg-clay ml-1 animate-pulse align-middle" />}
      </div>
    </div>
  );
}

function SuggestionChips({ onPick }) {
  return (
    <div className="flex flex-wrap gap-2 pt-1">
      {SUGGESTIONS.map((s) => (
        <button
          key={s}
          data-testid={`chat-suggestion-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          onClick={() => onPick(s)}
          className="text-xs border border-white/15 text-offwhite/60 hover:border-clay hover:text-offwhite px-3 py-2 transition-colors duration-300"
        >
          {s}
        </button>
      ))}
    </div>
  );
}

function ChatInputBar({ input, setInput, onSend, streaming }) {
  return (
    <div className="px-5 py-3 border-t border-white/10">
      <div className="flex items-center gap-2">
        <input
          data-testid="ai-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSend()}
          placeholder="Ask about products, MOQ, terms…"
          className="flex-1 bg-white/[0.05] border border-white/15 text-offwhite text-sm px-4 py-3 placeholder:text-offwhite/30 focus:outline-none focus:border-clay transition-colors duration-300"
        />
        <button
          data-testid="ai-chat-send"
          onClick={() => onSend()}
          disabled={streaming || !input.trim()}
          aria-label="Send message"
          className="w-11 h-11 shrink-0 bg-forest hover:bg-moss disabled:opacity-50 flex items-center justify-center transition-colors duration-300"
        >
          {streaming ? (
            <Loader2 className="w-4 h-4 text-offwhite animate-spin" />
          ) : (
            <Send className="w-4 h-4 text-offwhite" />
          )}
        </button>
      </div>
      <a
        href={waLink()}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="ai-chat-whatsapp-handoff"
        className="block mt-2.5 text-[11px] text-offwhite/40 hover:text-clay transition-colors duration-300"
      >
        Prefer a human? Chat with the HAQILA team on WhatsApp
      </a>
      <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" data-testid="chat-privacy-policy-link"
        className="inline-block mt-1 text-[11px] text-offwhite/40 hover:text-clay transition-colors duration-300">
        Privacy Policy
      </Link>
    </div>
  );
}

function ChatPanel({ messages, streaming, onSend, input, setInput }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const lastIndex = messages.length - 1;

  return (
    <motion.div
      data-testid="ai-chat-panel"
      initial={{ opacity: 0, y: 24, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 24, scale: 0.98 }}
      transition={{ duration: 0.35, ease: EASE }}
      className="fixed bottom-24 left-4 sm:left-6 z-50 w-[calc(100vw-2rem)] max-w-[400px] h-[540px] max-h-[calc(100vh-8rem)] bg-charcoal border border-white/15 shadow-2xl shadow-black/40 flex flex-col"
    >
      <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 bg-forest flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-offwhite" />
          </span>
          <div>
            <p className="font-display font-bold text-offwhite text-sm tracking-tight">HAQILA Assistant</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-offwhite/40">AI — Sourcing & Export</p>
          </div>
        </div>
      </div>

      <div data-testid="ai-chat-messages" className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {messages.map((msg, i) => (
          <MessageBubble
            key={msg.id}
            msg={msg}
            showCursor={streaming && i === lastIndex && msg.role === "assistant"}
          />
        ))}
        {messages.length === 1 && <SuggestionChips onPick={onSend} />}
        <div ref={bottomRef} />
      </div>

      <ChatInputBar input={input} setInput={setInput} onSend={onSend} streaming={streaming} />
    </motion.div>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const { messages, streaming, send } = useChatStream();
  const { pathname } = useLocation();

  if (pathname.startsWith("/admin")) return null;

  const handleSend = (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    send(message);
  };

  return (
    <>
      <button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen(!open)}
        aria-label="Ask HAQILA AI"
        className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-charcoal border border-clay/50 hover:border-clay flex items-center justify-center transition-colors duration-300 shadow-lg shadow-black/25"
      >
        {open ? <X className="w-5 h-5 text-offwhite" /> : <Sparkles className="w-5 h-5 text-clay" />}
      </button>

      <AnimatePresence>
        {open && (
          <ChatPanel messages={messages} streaming={streaming} onSend={handleSend} input={input} setInput={setInput} />
        )}
      </AnimatePresence>
    </>
  );
}
